import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { useMarketSummary, changeTone } from "../../lib/market";
import { fmtUsd, fmtPct } from "../../lib/format";
import { TokenLogo } from "../TokenSelect";
import { fileUrl } from "../TokenPageCustomize";
import { OriginalBadge } from "../OriginalBadge";
import { SectionHead } from "./HomeHero";
import { Empty } from "../State";

const Stat = ({ k, v, tone = "text-white", testid }) => <div><p className="text-slate-500">{k}</p><p className={`font-mono text-sm ${tone}`} data-testid={testid}>{v}</p></div>;

const FeaturedCard = ({ t, m, i }) => {
  const id = t.address.toLowerCase();
  return (
    <Link to={`/token/${t.address}`} data-testid={`featured-card-${id}`} style={{ animationDelay: `${i * 80}ms` }}
      className="neon-frame group relative block rounded-2xl bg-[#0b0d11]/95 p-6 backdrop-blur-xl transition-transform duration-300 hover:-translate-y-1 fade-up">
      <div className="flex items-center gap-4">
        <div className="rounded-full p-1 ring-2 ring-lime/40 shadow-[0_0_24px_rgba(57,255,20,0.25)]"><TokenLogo t={{ symbol: t.symbol, address: t.address, logo: t.logo || m?.logo }} size={56} /></div>
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 truncate font-display text-lg font-black text-white">{t.name || m?.name} <OriginalBadge address={t.address} /></p>
          <p className="font-mono text-xs text-slate-400">${t.symbol || m?.symbol}</p>
        </div>
      </div>
      <p className="mt-5 font-mono text-2xl font-bold text-lime" data-testid={`featured-price-${id}`}>{m?.available ? fmtUsd(m.price_usd) : m?.error ? "Market data unavailable" : m ? "No market yet" : "…"}</p>
      <div className="mt-4 grid grid-cols-3 gap-3 border-t border-white/10 pt-4 text-xs">
        <Stat k={m?.market_cap_usd ? "Market cap" : "FDV"} v={fmtUsd(m?.market_cap_usd ?? m?.fdv_usd)} testid={`featured-mcap-${id}`} />
        <Stat k="24h" v={fmtPct(m?.change_24h_pct)} tone={changeTone(m?.change_24h_pct)} testid={`featured-change-${id}`} />
        <Stat k="Volume 24h" v={fmtUsd(m?.volume_24h_usd)} testid={`featured-volume-${id}`} />
      </div>
      <span className="mt-5 block w-full rounded-lg border border-lime/40 py-2 text-center text-sm font-semibold text-lime transition-colors group-hover:bg-lime group-hover:text-black">View token profile</span>
    </Link>
  );
};

export const FeaturedTokens = () => {
  const { config } = useApp();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/listings", { params: { featured: true } }).then((r) => setList(r.data)).catch(() => setList([])); }, []);
  const tokens = [];
  if (config?.original_token) tokens.push({ address: config.original_token, symbol: "STING", name: "STING" });
  (list || []).forEach((l) => { if (!tokens.some((t) => t.address.toLowerCase() === l.contract_address)) tokens.push({ address: l.contract_address, symbol: l.onchain?.symbol, name: l.onchain?.name, logo: fileUrl(l.metadata?.logo_url) }); });
  const mkt = useMarketSummary(tokens.map((t) => t.address));
  return (
    <section data-testid="featured-tokens" className="fade-up">
      <SectionHead id="featured" title="Featured tokens" right={<Link to="/listings" data-testid="featured-get-listed" className="flex items-center gap-1 text-xs text-lime hover:underline"><Sparkles className="h-3.5 w-3.5" /> Get your token featured</Link>} />
      {list === null ? <div className="h-[300px] animate-pulse rounded-2xl bg-white/[0.03]" /> : !tokens.length ? <Empty testid="featured-empty" title="No featured tokens yet" hint="Featured placements are chosen by HIVEVORA admins." />
        : <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">{tokens.map((t, i) => <FeaturedCard key={t.address} t={t} m={mkt[t.address.toLowerCase()]} i={i} />)}</div>}
    </section>
  );
};
